import { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import type {AuthResponse} from "./useLogin.ts";
import {useToast} from "../components/common/toast/useToast.ts";

export const usePasswordReset = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { signIn } = useSignIn();
    const showToast = useToast();

    const requestReset = async (email: string): Promise<AuthResponse> => {
        setIsLoading(true);
        try {
            await signIn?.create({
                identifier: email,
                strategy: "reset_password_email_code",
            });

            // Store email for session management
            localStorage.setItem('resetEmail', email);

            showToast?.({
                type: 'success',
                title: 'Reset email sent',
                message: 'Check your email for password reset instructions'
            });

            return { success: true };
        } catch (err: unknown) {
            const error = err instanceof Error ? err.message : 'Reset request failed';
            showToast?.({
                type: 'error',
                title: 'Reset failed',
                message: error
            });
            return { success: false, error };
        } finally {
            setIsLoading(false);
        }
    };

    const resetPassword = async (code: string, newPassword: string): Promise<AuthResponse> => {
        setIsLoading(true);
        try {
            const result = await signIn?.attemptFirstFactor({
                strategy: "reset_password_email_code",
                code,
                password: newPassword,
            });

            if (result?.status === "complete") {
                showToast?.({
                    type: 'success',
                    title: 'Password reset successful',
                    message: 'You can now log in with your new password'
                });
                return { success: true };
            }

            return { success: false, error: 'Password reset failed' };
        } catch (err: unknown) {
            const error = err instanceof Error ? err.message : 'Password reset failed';
            showToast?.({
                type: 'error',
                title: 'Reset failed',
                message: error
            });
            return { success: false, error };
        } finally {
            setIsLoading(false);
        }
    };

    const clearResetSession = () => {
        localStorage.removeItem('resetEmail');
    };

    return { isLoading, requestReset, resetPassword, clearResetSession };
};